import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IoPlay, IoPause, IoPlaySkipForward } from "react-icons/io5";
import { usePlayer } from "../store/player";
import { useDailyQuota, triggerSubscriptionModal } from "../lib/quota";
import { ROUTES } from "../routes/paths";
import styles from "./MiniPlayer.module.css";

export function MiniPlayer({ isFullscreen = false }: { isFullscreen?: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { current, playing, isBuffering, toggle, next, close } = usePlayer();
  const quota = useDailyQuota();

  const pathname = location.pathname;

  // Suppress MiniPlayer on full PlayScreen, MTV video playback, and Auth routes
  const shouldHide =
    pathname === "/player" ||
    pathname === "/play" ||
    pathname === "/screen/PlayScreen" ||
    pathname.startsWith("/videos/") ||
    pathname.startsWith("/mtv/") ||
    pathname.startsWith("/screen/VideoPlayScreen") ||
    pathname.startsWith("/screen/VideoCommentsScreen") ||
    pathname === "/login" ||
    pathname === "/register" ||
    pathname === "/forgot-password" ||
    pathname === "/reset-password" ||
    pathname.startsWith("/auth/");

  const remaining = quota.isPremium ? null : quota.remaining;

  // Swipe / Pointer gestures to dismiss mini-player (dx < -50)
  useEffect(() => {
    const node = wrapperRef.current;
    if (!node) return;

    let startX: number | null = null;
    let dragging = false;

    function onPointerDown(e: PointerEvent) {
      if ((e.target as HTMLElement)?.closest("button")) return;
      dragging = true;
      startX = e.clientX;
      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp);
    }

    function onPointerMove(e: PointerEvent) {
      if (!dragging || startX === null) return;
      const dx = e.clientX - startX;
      if (dx < -50) {
        dragging = false;
        close();
        cleanup();
      }
    }

    function onPointerUp() {
      dragging = false;
      startX = null;
      cleanup();
    }

    function cleanup() {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    }

    node.addEventListener("pointerdown", onPointerDown);
    return () => {
      node.removeEventListener("pointerdown", onPointerDown);
      cleanup();
    };
  }, [close]);

  if (!current || shouldHide) return null;

  const thumbnailSrc = current.thumbnail || current.artwork || "/fallback-cover.png";
  const artistName = current.artist?.name || "Unknown Artist";

  const handleCardClick = () => {
    navigate(ROUTES.PLAYER);
  };

  return (
    <div
      ref={wrapperRef}
      className={`${styles.wrapper} ${isFullscreen ? styles.fullscreenWrapper : ""}`}
      role="region"
      aria-label="Mini Music Player"
      onClick={handleCardClick}
      style={{ cursor: "pointer" }}
    >
      <div className={styles.container}>
        <div className={styles.thumbnailContainer}>
          <img
            src={thumbnailSrc}
            alt=""
            className={`${styles.thumbnail} ${isBuffering ? styles.thumbnailBuffering : ""}`}
          />
          {isBuffering && (
            <div className={styles.bufferingOverlay}>
              <div className={styles.spinner} />
            </div>
          )}
        </div>

        <div className={styles.info}>
          <div className={styles.title} title={current.title}>
            {current.title}
          </div>
          <div className={styles.subInfo}>
            <span className={styles.artist} title={artistName}>
              {artistName}
            </span>
            {remaining !== null && (
              <span
                className={styles.pill}
                role="button"
                tabIndex={0}
                title="Daily free listens remaining. Click to upgrade to Premium."
                onClick={(e) => {
                  e.stopPropagation();
                  triggerSubscriptionModal();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                    triggerSubscriptionModal();
                  }
                }}
              >
                {remaining} left
              </span>
            )}
          </div>
        </div>

        <div className={styles.controls}>
          <button
            type="button"
            className={`${styles.controlButton} ${isBuffering ? styles.controlButtonDisabled : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              toggle();
            }}
            disabled={isBuffering}
            aria-label={playing ? "Pause" : "Play"}
          >
            {playing ? <IoPause size={30} /> : <IoPlay size={30} />}
          </button>
          <button
            type="button"
            className={`${styles.controlButton} ${isBuffering ? styles.controlButtonDisabled : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            disabled={isBuffering}
            aria-label="Next track"
          >
            <IoPlaySkipForward size={24} />
          </button>
        </div>
      </div>
    </div>
  );
}
